<template>
  <div class="grid-container mt-3">
    <section-header size="xl" variants="dividing">
      <heading level="1" size="xl" v-html="bp('orderHistory')"></heading>
    </section-header>
    <div>
      <!-- Filters & Buttons -->
      <section class="mb-4">
        <p id="section-description" v-html="bp('orderHistoryDescription')"></p>

        <p class="font-bold text-base my-4" v-html="bp('filterOrders')"></p>

        <div class="grid-x grid-margin-x">
          <div class="cell medium-5 large-3">
            <form-group
              v-model="options.orderNumber"
              type="text"
              name="orderNumber"
              :label="bp('orderNumber')"
              :maxlength="8"
              qaid="orderHistoryOrderNumber"
            ></form-group>

            <form-group
              v-model="options.invoiceNumber"
              type="text"
              name="invoiceNumber"
              :label="bp('invoiceNumber')"
              :maxlength="8"
              qaid="orderHistoryInvoiceNumber"
            ></form-group>

            <form-group
              v-model="options.partNumber"
              type="text"
              name="partNumber"
              :label="bp('partNumber')"
              :maxlength="25"
              qaid="orderHistoryPartNumber"
            ></form-group>

            <form-group
              v-model="options.customerPo"
              type="text"
              name="customerPo"
              :label="bp('customerPo')"
              :maxlength="25"
              qaid="orderHistoryCustomerPo"
            ></form-group>

            <form-group
              v-model="options.shipToNumber"
              v-validate="'max:8'"
              :error="bp('shipToInvalidMsg')"
              :show-error-messages="true"
              :class="{'has-error': !!errors.first('shipToNumber')}"
              type="text"
              name="shipToNumber"
              :label="bp('storeNumber')"
              :maxlength="8"
              qaid="orderHistoryStoreNumber"
              @input="updateShipToValue"
            ></form-group>
          </div>
          <div class="cell medium-7 large-6 md:flex">
            <fieldset class="form-fieldset mr-4">
              <form-group name="orderDate" :label="bp('orderDate')">
                <template v-slot:input>
                  <date-picker
                    id="order-date-input"
                    ref="orderPicker"
                    qaid="order-date-input"
                    :allow-input="true"
                    mode="range"
                    @change="orderDateInput"
                  />
                </template>
              </form-group>

              <form-group name="orderDate" :label="bp('invoiceDate')">
                <template v-slot:input>
                  <date-picker
                    id="invoice-date-input"
                    ref="invoicePicker"
                    qaid="invoice-date-input"
                    :allow-input="true"
                    mode="range"
                    @change="invoiceDateInput"
                  />
                </template>
              </form-group>
            </fieldset>

            <fieldset id="credit-memos-field" class="form-fieldset">
              <form-group
                type="radio"
                name="creditMemos"
                qaid="credit-memos-filter-radio"
                :label="bp('creditMemos')"
                :multi-items="creditMemosOptions"
                :value="options.creditMemoFilter"
                @change="handleCreditMemoUpdate"
              />

              <form-group
                type="radio"
                name="orders"
                qaid="order-filter-radio"
                :label="bp('orders')"
                :multi-items="orderOptions"
                :value="options.productTypeFilter || productTypeCustomFilter"
                @change="handleOrderUpdate"
              />

              <form-group
                class="lg:hidden"
                type="radio"
                name="orderStatusMobile"
                qaid="order-status-filter-radio"
                :label="bp('orderStatus')"
                :multi-items="orderStatusOptions"
                :value="options.orderStatusFilter || orderStatusCustomFilter"
                @change="handleOrderStatusUpdate"
              />
            </fieldset>
          </div>
          <div class="cell medium-12 large-3">
            <fieldset id="order-status-field" class="form-fieldset hidden lg:block">
              <form-group
                type="radio"
                name="orderStatus"
                qaid="order-status-filter-radio"
                :label="bp('orderStatus')"
                :multi-items="orderStatusOptions"
                :value="options.orderStatusFilter || orderStatusCustomFilter"
                @change="handleOrderStatusUpdate"
              />
            </fieldset>
            <div class="buttons buttons--right-for-md">
              <trek-button
                id="filter-orders-button"
                primary
                class="lg:w-full"
                qaid="filter-orders-button"
                @click="fetchAllOrders"
              >
                <span v-html="bp('filterOrders')"></span>
              </trek-button>
              <trek-button
                id="clear-filters-button"
                secondary
                class="lg:w-full"
                qaid="clear-filters-button"
                @click="clearChanges"
              >
                <span v-html="bp('clearFilters')"></span>
              </trek-button>
            </div>
          </div>
        </div>
      </section>

      <trek-loading page :is-loading="loading" />
      <!-- Orders Tabs -->
      <section id="order-tabs" class="my-5">
        <tabs id="order-tabs" ref="tabs" :value="defaultTab">
          <tab-pane v-for="tab in activeTabs" :key="tab.id" :name="tab.id">
            <h3 v-if="tab.id == 'tab-past-orders'" slot="label" class="heading heading--md m-0">
              <span v-html="tab.label"></span>
            </h3>
            <h3 v-else slot="label" class="heading heading--md m-0">
              <span v-html="tab.label"></span>
              ({{ orderData[tab.key].totalItemCount }})
            </h3>

            <order-history-grid
              :grid-data="orderData[tab.key]"
              :base-properties="baseProperties"
              :columns="columns"
              :part-search-columns="partSearchColumns"
              id-key="id"
              :order-status="tab.key"
              :child-columns="childColumns"
              :child-data-key="childDataKey"
              :link-manage-allocated-items="linkManageAllocatedItems"
              :is-part-search="isPartSearch"
            ></order-history-grid>
          </tab-pane>
        </tabs>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import pickBy from 'lodash/pickBy';

import SectionHeader from '@/components/SectionHeader';
import DatePicker from '@/components/DatePicker';
import OrderHistoryGrid from '@/components/containers/my-account/OrderHistoryGrid';
import TrekButton from '@/components/TrekButton';
import TrekLoading from '@/components/TrekLoading';
import FormGroup from '@/components/FormGroup';
import Heading from '@/components/Heading';
import Tabs from '@/components/tabs/Tabs';
import TabPane from '@/components/tabs/TabPane';

const ALL = 'All';
const INITIAL_OPT = {
  pageSize: 10,
  creditMemoFilter: ALL,
  invoiced: null,
  invoiceDateStart: null,
  invoiceDateEnd: null,
  onlyCreditMemos: null,
  orderDateStart: null,
  orderDateEnd: null,
  orderNumber: null,
  invoiceNumber: null,
  partNumber: null,
  productTypeFilter: null,
  customerPo: null,
  shipToNumber: null,
  customFilters: [],
  orderStatusFilter: ALL,
};

export default {
  components: {
    TrekLoading,
    OrderHistoryGrid,
    SectionHeader,
    DatePicker,
    FormGroup,
    Heading,
    Tabs,
    TabPane,
    TrekButton,
  },
  props: {
    baseProperties: {
      type: Array,
      default() {
        return [];
      },
    },
    isCartCheckout: {
      type: Boolean,
      default: false,
    },
    columns: {
      type: Array,
      default() {
        return [];
      },
    },
    partSearchColumns: {
      type: Array,
      default() {
        return [];
      },
    },
    idKey: {
      type: String,
      default: undefined,
    },
    childColumns: {
      type: Array,
      default() {
        return [];
      },
    },
    childDataKey: {
      type: String,
      default: undefined,
    },
    linkManageAllocatedItems: {
      type: String,
      default: undefined,
    },
    initialOrderNumber: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      loading: true,
      openOrders: {rows: [], pageIndex: 0, pageCount: 0, totalItemCount: 0},
      pastOrders: {rows: [], pageIndex: 0, pageCount: 0, totalItemCount: 0},
      pageSize: 10,
      options: cloneDeep(INITIAL_OPT),
      initialOptions: cloneDeep(INITIAL_OPT),
      isPartSearch: false,
      productTypeCustomFilter: ALL,
      orderStatusCustomFilter: null,
    };
  },

  computed: {
    creditMemosOptions() {
      return [
        {value: ALL, displayName: this.bp('showAllOrders')},
        {value: 'OnlyCreditMemos', displayName: this.bp('creditMemos')},
        {value: 'HideCreditMemos', displayName: this.bp('hideCreditMemos')},
      ];
    },

    orderOptions() {
      return [
        {value: ALL, displayName: this.bp('showAllOrders')},
        {value: 'ProjectOneBikes', displayName: this.bp('p1Orders')},
        {value: 'Consumer', displayName: this.bp('consumerOrders')},
        {value: 'Bikes', displayName: this.bp('bikes')},
        {value: 'Accessories', displayName: this.bp('accessories')},
      ];
    },

    orderStatusOptions() {
      return [
        {value: ALL, displayName: this.bp('showAllOrders')},
        {value: 'WithAllocatedItems', displayName: this.bp('allocatedItems')},
        {value: 'InWarehouse', displayName: this.bp('inWarehouseItems')},
        {value: 'Processing', displayName: this.bp('orderProcessing')},
      ];
    },

    activeTabs() {
      return this.tabs.filter((tab) => tab.active);
    },

    tabs() {
      return [
        {
          id: 'tab-open-orders',
          label: this.bp('openOrders'),
          data: this.openOrders,
          key: 'openOrders',
          active: this.isCartCheckout,
        },
        {
          id: 'tab-past-orders',
          label: this.bp('invoicedOrders'),
          data: this.pastOrders,
          key: 'pastOrders',
          active: true,
        },
      ];
    },

    defaultTab() {
      return this.isCartCheckout && this.openOrders.rows.length ? 'tab-open-orders' : 'tab-past-orders';
    },

    openOrdersData() {
      return this.openOrders;
    },

    orderData() {
      return {
        openOrders: this.openOrders,
        pastOrders: this.pastOrders,
      };
    },
  },

  provide() {
    return {
      container: this,
    };
  },

  created() {
    let url = new URL(window.location.href);
    let partNumber = url.searchParams.get('partNumber');
    this.options.partNumber = partNumber ? partNumber : this.initialOptions.partNumber;
    this.options.orderNumber = this.initialOrderNumber ? this.initialOrderNumber : this.initialOptions.orderNumber;
    this.fetchAllOrders();
  },

  methods: {
    handleCreditMemoUpdate(event) {
      this.$set(this.options, 'creditMemoFilter', event.currentTarget.value);
    },

    handleFilterUpdate(event, customFilterText, optionsFilterName, dataFilterName) {
      const index = this.options?.customFilters?.indexOf(customFilterText);
      if (event.currentTarget.value === customFilterText) {
        if (index === -1) {
          // adding filterValue to customFilters
          this.$set(this.options.customFilters, this.options.customFilters.length, event.currentTarget.value);
        }
        this[dataFilterName] = event.currentTarget.value;
        this.$set(this.options, optionsFilterName, null);
      } else {
        if (index !== -1) {
          // removing filterValue from customFilters
          this.options.customFilters?.splice(index, 1);
        }
        this[dataFilterName] = null;
        this.$set(this.options, optionsFilterName, event.currentTarget.value);
      }
    },

    handleOrderUpdate(event) {
      if (event.currentTarget.value === ALL) {
        this.productTypeCustomFilter = event.currentTarget.value;
        this.$set(this.options, 'productTypeFilter', null);
        const index = this.options?.customFilters?.indexOf('Consumer');
        if (index !== -1) {
          // removing consumer from customFilters for All as selected value
          this.options.customFilters?.splice(index, 1);
        }
      } else {
        this.handleFilterUpdate(event, 'Consumer', 'productTypeFilter', 'productTypeCustomFilter');
      }
    },

    handleOrderStatusUpdate(event) {
      this.handleFilterUpdate(event, 'WithAllocatedItems', 'orderStatusFilter', 'orderStatusCustomFilter');
    },

    clearChanges() {
      this.options = cloneDeep(this.initialOptions);
      this.productTypeCustomFilter = ALL;
      this.$refs.orderPicker.fp.clear();
      this.$refs.invoicePicker.fp.clear();
      this.fetchAllOrders();
    },

    fetchAllOrders() {
      this.fetchOrders('openOrders');
      this.fetchOrders('pastOrders');
    },

    fetchOrders(status = 'openOrders') {
      this.loading = true;
      let param = {
        pageIndex: this[status].pageIndex,
        ...this.options,
      };
      param.customFilters.length ? '' : delete param.customFilters;
      param = pickBy(param); // Removing null properties
      axios
        .get(`/my-account/${status.toString().toLowerCase()}`, {
          params: new URLSearchParams(param),
        })
        .then(
          ({
            data: {
              data: {orders, paging},
            },
          }) => {
            this[status] = {
              rows: orders,
              pageIndex: paging.pageIndex || 0,
              pageCount: paging.pageCount || 0,
              totalItemCount: paging.totalItemCount || 0,
            };

            this.isPartSearch = this.options && this.options.partNumber && this.options.partNumber.length > 0;

            // The tab labels don't like to update on their own, so:
            this.$refs.tabs.$refs.nav.$forceUpdate();
          }
        )
        .finally(() => {
          this.loading = false;
        });
    },

    invoiceDateInput({startDate, endDate}) {
      if (startDate) {
        this.options.invoiceDateStart = window.flatpickr.formatDate(startDate, 'm/d/Y');
      }
      if (endDate) {
        this.options.invoiceDateEnd = window.flatpickr.formatDate(endDate, 'm/d/Y');
      }
    },

    orderDateInput({startDate, endDate}) {
      if (startDate) {
        this.options.orderDateStart = window.flatpickr.formatDate(startDate, 'm/d/Y');
      }
      if (endDate) {
        this.options.orderDateEnd = window.flatpickr.formatDate(endDate, 'm/d/Y');
      }
    },
    updateShipToValue() {
      const shipToMaxLength = 8;
      this.options.shipToNumber = this.options.shipToNumber.replace(/[^\d]/g, '');
      if (String(this.options.shipToNumber).length > shipToMaxLength) {
        this.options.shipToNumber = this.options.shipToNumber.substring(0, shipToMaxLength);
      }
      this.$forceUpdate();
    },
  },
};
</script>
